<script module lang="ts">
  import type { EntitySearchResult } from '#server/controllers/search/lib/normalize_result'

  export type EntityAutocompleteSuggestion = SerializedEntity | EntitySearchResult
</script>
<script lang="ts">
  import { map, debounce } from 'lodash-es'
  import { autofocus as autofocusFn } from '#app/lib/components/actions/autofocus'
  import { normalizeError, type ContextualizedError } from '#app/lib/error'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import { logError } from '#app/lib/loggers'
  import { wait } from '#app/lib/promises'
  import { getViewportHeight, onScrollToBottom } from '#app/lib/screen'
  import { arrayIncludes, assertDefined } from '#app/lib/utils'
  import type { ClickEvent, KeyDownEvent } from '#app/types/declarations/svelte'
  import { getDefaultSuggestions } from '#entities/components/editor/lib/suggestions/get_suggestions_per_properties'
  import { propertiesWithValuesShortlists } from '#entities/components/editor/lib/suggestions/property_values_shortlist'
  import { createByProperty } from '#entities/lib/create_entities'
  import type { SerializedEntity } from '#entities/lib/entities'
  import { searchEntitiesByTypes, type SearchableType } from '#entities/lib/search/type_search'
  import { entityTypeNameBySingularType } from '#entities/lib/types/entities_types'
  import Spinner from '#general/components/spinner.svelte'
  import SearchMaintenanceFlash from '#search/components/search_maintenance_flash.svelte'
  import type { EntityUri, ExtendedEntityType, PropertyUri } from '#server/types/entity'
  import { I18n, i18n } from '#user/lib/i18n'
  import EntitySuggestion from './entity_suggestion.svelte'

  let {
    searchTypes,
    claimFilter,
    currentEntityUri,
    currentEntityLabel = $bindable(),
    placeholder,
    allowEntityCreation = false,
    showDefaultSuggestions = true,
    createdEntityType,
    createOnWikidata = false,
    relationSubjectEntity,
    relationProperty,
    displaySuggestionType = false,
    autofocus = true,
    showSuggestions = $bindable(false),
    title,
    onSelect,
    onClose,
    onError = logError,
    onFocus,
    onInputClick,
  }: {
    searchTypes: SearchableType[]
    claimFilter?: PropertyUri
    currentEntityUri?: EntityUri
    currentEntityLabel?: string
    placeholder?: string
    allowEntityCreation?: boolean
    showDefaultSuggestions?: boolean
    createdEntityType?: ExtendedEntityType
    createOnWikidata?: boolean
    relationSubjectEntity?: EntityAutocompleteSuggestion
    relationProperty?: PropertyUri
    displaySuggestionType?: boolean
    autofocus?: boolean
    showSuggestions?: boolean
    title?: string
    onSelect: (entity: EntityAutocompleteSuggestion) => void
    onClose?: () => void
    onError?: (err: ContextualizedError) => void
    onFocus?: () => void
    onInputClick?: (e: ClickEvent) => void
  } = $props()

  let input: HTMLInputElement | undefined = $state()
  const initialEntityLabel = currentEntityLabel

  let suggestions: EntityAutocompleteSuggestion[] = $state([])
  let scrollableElement: HTMLElement | undefined = $state()

  function onInputKeydown (e: KeyDownEvent) {
    const key = getActionKey(e)
    if (key === 'esc') {
      close()
    } else if (key === 'enter') {
      if (suggestions[highlightedIndex]) {
        onSelect(suggestions[highlightedIndex])
      }
    } else if (key === 'down') {
      highlightedIndex = highlightedIndex + 1
      e.preventDefault()
    } else if (key === 'up') {
      highlightedIndex = highlightedIndex - 1
      e.preventDefault()
    } else {
      lazySearch()
    }
  }

  let lastSearch: string
  let waitForSearch: ReturnType<typeof searchEntitiesByTypes> | undefined = $state()
  let searchText = $state(currentEntityLabel || '')
  let limit = 10
  let offset = 0
  let fetching = $state(false)
  let canFetchMore = $state(true)
  interface SearchOptions {
    fetchMore?: boolean
  }
  // TODO: detect uris and get the corresponding entities
  async function search (options: SearchOptions = {}) {
    const { fetchMore = false } = options
    searchText = input?.value || ''
    if (isNotSearchableServerSide) return filterExistingSuggestions(searchText)
    try {
      if (searchText.length === 0) return
      if (searchText === lastSearch) {
        if (fetching || !fetchMore || !canFetchMore) return
        offset += limit
        limit += 10
      } else {
        limit = 10
        offset = 0
        highlightedIndex = 0
        suggestions = []
        canFetchMore = true
      }
      lastSearch = searchText
      waitForSearch = searchEntitiesByTypes({ types: searchTypes, search: searchText, limit, offset, claim: claimFilter })
      showSuggestions = true
      fetching = true
      const res = await waitForSearch
      if (searchText === lastSearch) {
        // @ts-expect-error
        suggestions = [ ...suggestions, ...getNewSuggestions(res.results) ]
        showSuggestions = true
        setTimeout(scrollToSuggestionsDropdownIfNeeded, 50)
        canFetchMore = 'continue' in res && res.continue != null
      }
    } catch (err) {
      showSuggestions = false
      onError(normalizeError(err))
    } finally {
      fetching = false
    }
  }

  function getNewSuggestions (newSuggestions: EntityAutocompleteSuggestion[]) {
    const currentSuggestionsUris = new Set(map(suggestions, 'uri'))
    return newSuggestions.filter(suggestion => !currentSuggestionsUris.has(suggestion.uri))
  }

  function filterExistingSuggestions (searchText: string) {
    // Display all suggestions if search text is the initial label value,
    // to avoid filtering on an existing value, and instead dislay all those default suggestions.
    if (searchText === '' || searchText === initialEntityLabel) {
      if (defaultSuggestions) {
        suggestions = defaultSuggestions
        return
      }
    }
    suggestions = defaultSuggestions?.filter(matchLabelOrDescription) || []
  }

  const matchLabelOrDescription = (entity: SerializedEntity) => entity.label?.match(searchText) || entity.description?.match(searchText)

  const lazySearch = debounce(search, 200)

  // svelte-ignore state_referenced_locally
  if (currentEntityLabel && autofocus) lazySearch()

  let showSuggestionsStateBeforeBlur = false
  function _onFocus () {
    onFocus?.()
    lazySearch()
    if (showSuggestionsStateBeforeBlur) showSuggestions = true
  }

  async function onBlur () {
    showSuggestionsStateBeforeBlur = showSuggestions
    // If the focus is lost because the user clicked on one of the suggestions,
    // let EntitySuggestion 'select' event be acted upon before the component
    // gets destroyed due to `showSuggestions = false`
    // Somehow, waiting for the next tick isn't enough.
    await wait(200)
    showSuggestions = false
  }

  let highlightedIndex = $state(0)

  $effect(() => {
    if (suggestions) {
      const lastIndex = suggestions.length - 1
      if (highlightedIndex < 0) highlightedIndex = 0
      else if (!canFetchMore && highlightedIndex > lastIndex) {
        highlightedIndex = 0
      }
    }
  })

  async function create () {
    try {
      assertDefined(relationSubjectEntity)
      assertDefined(relationProperty)
      const createdEntity = await createByProperty({
        relationSubjectEntity,
        property: relationProperty,
        name: searchText,
        createOnWikidata,
      })
      onSelect(createdEntity)
    } catch (err) {
      onError(normalizeError(err))
    }
  }

  function close (e?: ClickEvent) {
    showSuggestions = false
    onClose?.()
    e?.stopPropagation()
  }

  let defaultSuggestions: SerializedEntity[] | undefined = $state()
  async function fetchDefaultSuggestions () {
    try {
      showSuggestions = true
      fetching = true
      assertDefined(relationSubjectEntity)
      assertDefined(relationProperty)
      defaultSuggestions ??= await getDefaultSuggestions({
        entity: relationSubjectEntity,
        property: relationProperty,
      })
      fetching = false
      if (defaultSuggestions && canDefaultSuggestionsBeDisplayed) suggestions = defaultSuggestions
    } catch (err) {
      showSuggestions = false
      onError(normalizeError(err))
    }
  }

  const isNotSearchableServerSide = $derived(arrayIncludes(propertiesWithValuesShortlists, relationProperty))
  const canDefaultSuggestionsBeDisplayed = $derived((isNotSearchableServerSide || (showDefaultSuggestions && searchText === '')) && (relationSubjectEntity?.type != null))

  $effect(() => {
    if (canDefaultSuggestionsBeDisplayed) fetchDefaultSuggestions()
  })

  let autocompleteDropdownEl: HTMLElement | undefined = $state()
  function scrollToSuggestionsDropdownIfNeeded () {
    if (!autocompleteDropdownEl) return
    const dropdownRect = autocompleteDropdownEl.getBoundingClientRect()
    if (dropdownRect.bottom > getViewportHeight()) {
      autocompleteDropdownEl.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' })
    }
  }
</script>

<div class="entity-autocomplete-selector">
  {#if searchText !== ''}
    <SearchMaintenanceFlash />
  {/if}

  <div class="input-group">
    <div class="input-wrapper">
      <input
        type="text"
        onclick={onInputClick}
        bind:value={currentEntityLabel}
        onkeydown={onInputKeydown}
        onfocus={_onFocus}
        onblur={onBlur}
        bind:this={input}
        use:autofocusFn={{ disabled: autofocus !== true }}
        class:has-entity-uri={currentEntityUri != null}
        title={title || I18n('search for an entity')}
        {placeholder}
      />
      {#if currentEntityUri}
        <span class="uri">{currentEntityUri}</span>
      {/if}
    </div>
    {#if suggestions && showSuggestions && (searchText !== '' || suggestions.length > 0)}
      <div class="autocomplete" bind:this={autocompleteDropdownEl}>
        <div
          class="suggestions-wrapper"
          onscroll={onScrollToBottom(() => search({ fetchMore: true }))}
          bind:this={scrollableElement}
        >
          <ul class="suggestions">
            {#each suggestions as suggestion, i (suggestion.uri)}
              <EntitySuggestion
                {suggestion}
                {displaySuggestionType}
                {scrollableElement}
                displayedProperty={claimFilter}
                highlight={i === highlightedIndex}
                {onSelect}
              />
            {/each}
          </ul>
          {#await (waitForSearch || defaultSuggestions)}
            <div class="spinner-wrapper"><Spinner /></div>
          {:then}
            {#if suggestions.length === 0}
              <p class="no-result">{i18n('no result')}</p>
            {/if}
          {/await}
        </div>
        <div class="controls">
          <button
            class="close"
            onclick={close}
          >
            {@html icon('close')}
            {I18n('close')}
          </button>
          {#if allowEntityCreation && searchText.length > 0}
            <button
              class="create"
              onclick={create}
            >
              {@html icon('plus')}
              {I18n(`create a new ${entityTypeNameBySingularType[createdEntityType]}`)}: "{searchText}"
            </button>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  @import "general/scss/utils";
  .entity-autocomplete-selector{
    flex: 1;
  }
  .input-group{
    font-weight: normal;
    position: relative;
    margin-inline-end: 0.5em;
    margin-block-end: 0;
    inline-size: 100%;
  }
  .input-wrapper{
    inline-size: 100%;
    position: relative;
    input{
      flex: 1;
      block-size: 100%;
      font-weight: normal;
      margin-block-end: 0.1em;
      @include transition(padding, 0.2s);
      &.has-entity-uri{
        padding: 0.5em 0.5em 0.8em;
      }
      &:not(.has-entity-uri){
        padding: 0.5em;
      }
    }
    .uri{
      position: absolute;
      inset-inline-end: 0.5em;
      inset-block-end: 0.2em;
    }
  }
  .autocomplete{
    background-color: white;
    overflow: hidden;
    @include display-flex(column, center, center);
    @include shy-border(0.5);
    /* Small screens */
    @media screen and (width < $small-screen){
      inline-size: 100%;
    }
    /* Larger screens */
    @media screen and (width >= $very-small-screen){
      position: absolute;
      inset-block-start: 100%;
      inset-inline: -1px;
      z-index: 1;
    }
  }
  .suggestions-wrapper{
    position: relative;
    max-block-size: 15rem;
    overflow: auto;
    align-self: stretch;
  }
  .spinner-wrapper{
    @include display-flex(row, center, center);
  }
  .controls{
    @include display-flex(row, center, space-between);
    inline-size: 100%;
    background-color: #ddd;
    button{
      font-weight: normal;
      margin: 0.5em;
      padding: 0.5em;
    }
  }
  .close{
    @include bg-hover($light-grey);
    color: #333;
  }
  .create{
    margin-inline-start: auto;
    color: white;
    @include bg-hover($success-color);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .uri{
    font-size: 0.7rem;
    font-family: sans-serif;
    color: #888;
  }
  .no-result{
    text-align: center;
    padding: 0.5em;
  }
</style>
